import { computed, onBeforeMount, ref, watch } from "vue";
// Global variables
const colorMap = ref(new Map());
const isProcessing = ref(false);
// One webpack runtime makes colorMap page-global, so a single incomplete scan would otherwise
// serve every island. Track which sheets have been read rather than how many: later arrivals are
// still picked up, and a sheet already read is never re-fetched.
const readStyleSheets = new WeakSet();
// Function to process each stylesheet
const processStyleSheet = (styleSheet) => {
    try {
        const cssRules = styleSheet.cssRules;
        for (const rule of Array.from(cssRules)) {
            if (rule instanceof CSSStyleRule) {
                const style = rule.style;
                for (let i = 0; i < style.length; i++) {
                    const propertyName = style[i];
                    if (propertyName.match(/^--[a-z-]+(-[0-9]{1,3})?$/) && !propertyName.match(/^--hs(ds)?-.+$/)) {
                        colorMap.value.set(propertyName, style.getPropertyValue(propertyName).trim());
                    }
                }
            }
            else if (rule instanceof CSSImportRule) {
                processStyleSheet(rule.styleSheet);
            }
        }
    }
    catch (e) {
        console.warn("Could not access stylesheet:", styleSheet.href, e);
    }
};
const unreadStyleSheets = () => Array.from(document.styleSheets).filter((sheet) => !sheet.disabled && !readStyleSheets.has(sheet) && (!sheet.href || sheet.href.startsWith(window.location.origin)));
const getAllCSSVariables = async (styleSheets) => await Promise.all(styleSheets.map(async (sheet) => {
    if (sheet.href)
        await fetch(sheet.href);
    processStyleSheet(sheet);
    // Marked only once read, so a sheet whose fetch failed is retried on the next mount.
    readStyleSheets.add(sheet);
}));
const colorGroups = [
    {
        group: "dark",
        colors: [
            "red-lighten-2",
            "pink-lighten-2",
            "purple-lighten-2",
            "deep-purple-lighten-2",
            "indigo-lighten-2",
            "blue-lighten-2",
            "light-blue-lighten-2",
            "cyan-lighten-2",
            "teal-lighten-2",
            "green-lighten-2",
            "light-green-lighten-2",
            "lime-lighten-2",
            "yellow-lighten-2",
            "amber-lighten-2",
            "orange-lighten-2",
            "deep-orange-lighten-2",
            "brown-lighten-2",
            "blue-grey-lighten-2"
        ]
    },
    {
        group: "light",
        colors: [
            "red-lighten-4",
            "pink-lighten-4",
            "purple-lighten-4",
            "deep-purple-lighten-4",
            "indigo-lighten-4",
            "blue-lighten-4",
            "light-blue-lighten-4",
            "cyan-lighten-4",
            "teal-lighten-4",
            "green-lighten-4",
            "light-green-lighten-4",
            "lime-lighten-4",
            "yellow-lighten-4",
            "amber-lighten-4",
            "orange-lighten-4",
            "deep-orange-lighten-4",
            "brown-lighten-4",
            "blue-grey-lighten-4"
        ]
    }
];
export const useColors = () => {
    onBeforeMount(async () => {
        if (isProcessing.value)
            return;
        isProcessing.value = true;
        try {
            // Sheets keep arriving while we await — GlobalStylesInjectionPlugin is async and every
            // defineAsyncComponent injects on chunk load — so one pass can finish against a list that
            // has already grown. Drain until nothing new appears: an island that skipped this scan
            // because it was already running has no later mount guaranteed to pick up the remainder.
            for (let sheets = unreadStyleSheets(); sheets.length; sheets = unreadStyleSheets()) {
                await getAllCSSVariables(sheets);
            }
        }
        catch (e) {
            console.warn("Could not read all CSS variables:", e);
        }
        finally {
            // isValidMDColor resolves off a watcher on isProcessing, so stranding it true would leave
            // that promise permanently unresolved for every island on the page.
            isProcessing.value = false;
        }
    });
    const mdColors = computed(() => Array.from(colorMap.value.keys())
        .filter((name) => name.startsWith("--color-") && !name.startsWith("--color-available-"))
        .map((name) => name.replace(/^--color-/, "")));
    const getColorGroups = () => {
        return colorGroups.map((group) => group.colors).flat();
    };
    const getUnusedColors = (usedColors) => {
        return getColorGroups().filter((color) => !usedColors.includes(color));
    };
    const getNextLessUsedColors = (usedColors) => {
        const occMap = new Map();
        usedColors
            .filter((color) => !occMap.has(color))
            .forEach((color) => occMap.set(color, usedColors.filter((c) => c === color).length));
        return Array.from(occMap.entries()).sort((a, b) => a[1] - b[1])[0][0];
    };
    const getNextUnusedColor = (usedColors) => {
        return getUnusedColors(usedColors)[0] ?? getNextLessUsedColors(usedColors);
    };
    const isValidMDColor = async (color) => {
        if (isProcessing.value) {
            return new Promise((resolve) => {
                const unwatch = watch(isProcessing, async (next) => {
                    if (next)
                        return;
                    unwatch();
                    resolve(await isValidMDColor(color));
                });
            });
        }
        return mdColors.value.includes(color);
    };
    const isValidMDBaseColor = async (color) => {
        if (isProcessing.value) {
            return new Promise((resolve) => {
                const unwatch = watch(isProcessing, async (next) => {
                    if (next)
                        return;
                    unwatch();
                    resolve(await isValidMDBaseColor(color));
                });
            });
        }
        return mdColors.value.includes(color) && !color.match(/-(lighten|accent|darken)-[0-9]+$/);
    };
    const getLightColorVariant = (colorName) => {
        if (!colorName.match(/-\d$/)) {
            return `${colorName}-lighten-2`;
        }
        return colorName.replace(/-4$/, "-5").replace(/-3$/, "-5").replace(/-2$/, "-4").replace(/-1$/, "-3");
    };
    const getColor = (color, lightVariant) => {
        return lightVariant ? getLightColorVariant(color) : color;
    };
    return {
        colorGroups,
        getColor,
        getNextUnusedColor,
        isValidMDColor,
        isValidMDBaseColor
    };
};
